import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  BookOpen, 
  Download, 
  Eye, 
  Users, 
  Globe,
  Filter,
  TrendingUp,
  Clock,
  SortAsc,
  Library as LibraryIcon,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Language code to name mapping
const LANGUAGE_NAMES = {
  'en': 'English',
  'es': 'Spanish',
  'fr': 'French',
  'de': 'German',
  'it': 'Italian',
  'pt': 'Portuguese',
  'ru': 'Russian',
  'zh': 'Chinese',
  'ja': 'Japanese',
  'ko': 'Korean',
  'ar': 'Arabic',
  'hi': 'Hindi',
  'nl': 'Dutch',
  'sv': 'Swedish',
  'no': 'Norwegian',
  'da': 'Danish',
  'fi': 'Finnish',
  'pl': 'Polish',
  'tr': 'Turkish',
  'he': 'Hebrew',
  'el': 'Greek',
  'th': 'Thai',
  'vi': 'Vietnamese',
  'id': 'Indonesian',
  'ms': 'Malay',
  'tl': 'Tagalog',
  'uk': 'Ukrainian',
  'cs': 'Czech',
  'ro': 'Romanian',
  'hu': 'Hungarian',
  'hbo': 'Biblical Hebrew',
  'grc': 'Koine Greek',
  'fa': 'Persian',
  'xh': 'Xhosa',
  'sw': 'Swahili',
  'zu': 'Zulu',
  'af': 'Afrikaans',
  'ca': 'Catalan',
  'hr': 'Croatian',
  'bg': 'Bulgarian',
  'sk': 'Slovak',
  'sr': 'Serbian',
  'sl': 'Slovenian',
  'ur': 'Urdu',
  'bn': 'Bengali',
  'ta': 'Tamil',
  'ne': 'Nepali',
  'am': 'Amharic',
};

// Language code to country flag code (ISO 3166-1 alpha-2)
const LANG_TO_FLAG = {
  'en': 'gb', 'es': 'es', 'fr': 'fr', 'de': 'de', 'it': 'it',
  'pt': 'br', 'ru': 'ru', 'zh': 'cn', 'ja': 'jp', 'ko': 'kr',
  'ar': 'sa', 'hi': 'in', 'nl': 'nl', 'sv': 'se', 'no': 'no',
  'da': 'dk', 'fi': 'fi', 'pl': 'pl', 'tr': 'tr', 'he': 'il',
  'el': 'gr', 'th': 'th', 'vi': 'vn', 'id': 'id', 'ms': 'my',
  'tl': 'ph', 'uk': 'ua', 'cs': 'cz', 'ro': 'ro', 'hu': 'hu',
  'hbo': 'il', 'grc': 'gr', 'fa': 'ir', 'xh': 'za', 'sw': 'tz',
  'zu': 'za', 'af': 'za', 'ca': 'es', 'hr': 'hr', 'bg': 'bg',
  'sk': 'sk', 'sr': 'rs', 'sl': 'si', 'ur': 'pk', 'bn': 'bd',
  'ta': 'in', 'ne': 'np', 'am': 'et',
};

const getLangName = (text) => {
  if (!text.language) return 'Unknown';
  if (typeof text.language === 'object') {
    return text.language.name || LANGUAGE_NAMES[text.language.code] || text.language.code || 'Unknown';
  }
  return LANGUAGE_NAMES[text.language] || text.language;
};

const getLangCode = (text) => {
  if (!text.language) return '';
  if (typeof text.language === 'object') return text.language.code || '';
  return text.language;
};

const FlagImg = ({ code, size = 20 }) => {
  const flagCode = LANG_TO_FLAG[code] || code;
  if (!flagCode) return null;
  return (
    <img 
      src={`https://flagcdn.com/w20/${flagCode}.png`}
      srcSet={`https://flagcdn.com/w40/${flagCode}.png 2x`}
      alt={code}
      className="inline-block rounded-sm flex-shrink-0"
      style={{ width: size, height: Math.round(size * 0.75) }}
      onError={(e) => { e.target.style.display = 'none'; }}
    />
  );
};

export default function Library() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [texts, setTexts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(null);
  const [stats, setStats] = useState({ total_texts: 0, total_imports: 0, language_count: 0 });
  const [error, setError] = useState(null);
  const [series, setSeries] = useState([]);
  
  // Filters
  const [search, setSearch] = useState('');
  const [language, setLanguage] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  
  // Filter options
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  
  // Pagination
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const limit = 12;

  // Fetch categories and languages
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/library/categories`, { withCredentials: true });
        setLanguages(res.data.languages || []);
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Failed to fetch filters:', err);
      }
    };
    fetchFilters();
    fetchStats();
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/series`, { withCredentials: true });
        setSeries(res.data.series || []);
      } catch (err) {
        console.error('Failed to fetch series:', err);
      }
    })();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/library/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch texts
  const fetchTexts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (language) params.append('language', language);
      if (category) params.append('category', category);
      params.append('sort_by', sortBy);
      params.append('limit', String(limit));
      params.append('offset', String(page * limit));

      const res = await axios.get(
        `${BACKEND_URL}/api/library/texts?${params.toString()}`,
        { withCredentials: true }
      );
      setTexts(res.data.texts || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch texts:', err);
      setError('Failed to load library. Please try again.');
      toast.error(t('library.loadError'));
    } finally {
      setLoading(false);
    }
  }, [search, language, category, sortBy, page]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchTexts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchTexts]);

  const handleImport = async (textId, title) => {
    setImporting(textId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/library/import/${textId}`,
        {},
        { withCredentials: true }
      );
      toast.success(`"${title}" imported to your library!`);
      fetchStats();
      fetchTexts(); // Refresh to update import counts
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to import text';
      toast.error(message);
    } finally {
      setImporting(null);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setLanguage('');
    setCategory('');
    setSortBy('popular');
    setPage(0);
  };

  const hasFilters = search || language || category || sortBy !== 'popular';
  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <LibraryIcon className="h-8 w-8 text-primary" />
              Public Library
            </h1>
            <p className="text-muted-foreground mt-1">
              Discover and import texts shared by the community
            </p>
          </div>
          
          {/* Stats */}
          <div className="flex gap-4">
            <div className="text-center px-4 py-2 bg-muted rounded-lg">
              <div className="text-xl font-bold text-primary">{stats.total_texts}</div>
              <div className="text-xs text-muted-foreground">Texts</div>
            </div>
            <div className="text-center px-4 py-2 bg-muted rounded-lg">
              <div className="text-xl font-bold text-green-500">{stats.total_imports}</div>
              <div className="text-xs text-muted-foreground">{t('library.stats.imports')}</div>
            </div>
            <div className="text-center px-4 py-2 bg-muted rounded-lg">
              <div className="text-xl font-bold text-blue-500">{stats.language_count}</div>
              <div className="text-xs text-muted-foreground">{t('library.stats.languages')}</div>
            </div>
          </div>
        </div>

        {/* Story Series */}
        {series.length > 0 && (
          <Card data-testid="series-section">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-primary" />
                    Story Series
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Themed, levelled reading collections
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/series')}
                  data-testid="series-see-all"
                >
                  See all <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {series.slice(0, 4).map((s) => (
                  <button
                    key={s.series_id}
                    onClick={() => navigate(`/series/${s.series_id}`)}
                    className="text-left border rounded-lg p-3 hover:border-primary hover:shadow-md transition-all group"
                    data-testid={`library-series-tile-${s.series_id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl" aria-hidden>{s.cover_emoji || '📖'}</span>
                      <FlagImg code={s.language_code} size={20} />
                    </div>
                    <h3 className="font-semibold text-sm line-clamp-2 group-hover:text-primary transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {s.episode_count} episodes · {s.cefr_level}
                    </p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('library.filters.search')}
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  className="pl-10"
                  data-testid="library-search"
                />
              </div>

              {/* Language Filter */}
              <Select value={language} onValueChange={(v) => { setLanguage(v === 'all' ? '' : v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="library-language-filter">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('library.filters.language')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  {languages.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      <span className="flex items-center gap-2">
                        <FlagImg code={lang.code} size={16} />
                        {LANGUAGE_NAMES[lang.code] || lang.name || lang.code} ({lang.count})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Category Filter */}
              <Select value={category} onValueChange={(v) => { setCategory(v === 'all' ? '' : v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-[180px]" data-testid="library-category-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('library.filters.category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="library-sort">
                  <SortAsc className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('library.filters.sort')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">
                    <span className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" /> Popular
                    </span>
                  </SelectItem>
                  <SelectItem value="recent">
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4" /> Recent
                    </span>
                  </SelectItem>
                  <SelectItem value="title">
                    <span className="flex items-center gap-2">
                      <SortAsc className="h-4 w-4" /> Title A-Z
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Clear Filters */}
              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} title="Clear filters">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Info */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {loading ? 'Loading...' : `${total} text${total !== 1 ? 's' : ''} found`}
          </span>
          {totalPages > 1 && (
            <span>Page {page + 1} of {totalPages}</span>
          )}
        </div>

        {/* Text Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-16 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Library</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button variant="outline" onClick={fetchTexts}>
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : texts.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No texts found</h3>
              <p className="text-muted-foreground mb-4">
                {hasFilters 
                  ? 'Try adjusting your filters'
                  : 'Be the first to share a text with the community!'}
              </p>
              {hasFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {texts.map((text) => (
              <Card 
                key={text.text_id} 
                className="hover:shadow-lg transition-shadow group cursor-pointer"
                data-testid={`library-text-${text.text_id}`}
                onClick={() => navigate(`/texts/${text.text_id}?source=library`)}
              >
                <CardContent className="p-4 space-y-3">
                  {/* Header */}
                  <div>
                    <h3 className="font-semibold line-clamp-2 group-hover:text-primary transition-colors">
                      {text.title || 'Untitled'}
                    </h3>
                    {text.author && (
                      <p className="text-sm text-muted-foreground">by {text.author}</p>
                    )}
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary" className="text-xs gap-1.5">
                      <FlagImg code={getLangCode(text)} size={16} />
                      {getLangName(text)}
                    </Badge>
                    {text.category && (
                      <Badge variant="outline" className="text-xs">
                        {text.category}
                      </Badge>
                    )}
                  </div>

                  {/* Description */}
                  {text.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {text.description}
                    </p>
                  )}

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" />
                      {(text.word_count || 0).toLocaleString()} words
                    </span>
                    <span className="flex items-center gap-1">
                      <Download className="h-3 w-3" />
                      {text.import_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {text.view_count || 0}
                    </span>
                  </div>

                  {/* Creator */}
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    Shared by {text.creator_name}
                  </div>

                  {/* Import Button */}
                  <Button
                    className="w-full"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handleImport(text.text_id, text.title); }}
                    disabled={importing === text.text_id}
                    data-testid={`import-${text.text_id}`}
                  >
                    {importing === text.text_id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Import to My Library
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (page < 3) {
                  pageNum = i;
                } else if (page > totalPages - 4) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={page === pageNum ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum + 1}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
